import { SchmancyElement } from '@schmancy/mixins/index'
// Composition dependencies: an unregistered <schmancy-skeleton> or
// <schmancy-surface> never upgrades, so the pending and failed states would
// reserve the box and paint nothing inside it.
import '../icons'
import '../skeleton'
import '../surface'
import '../typography'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { choose } from 'lit/directives/choose.js'

/** The CSS box a raster was captured from — the size it is meant to be laid out at. */
export interface SchmancyMediaBox {
	width: number
	height: number
}

/**
 * @element schmancy-media-frame
 *
 * A frame whose size comes from geometry the consumer already recorded, not
 * from the raster inside it.
 *
 * A capture's intrinsic size is device pixels: a screenshot of a 240×160 CSS
 * box on a 2× display decodes as 480×320, and on a 3× display as 720×480, so
 * the image's own dimensions are never the dimensions to lay out at — the same
 * element would render at three different sizes on three machines. The box
 * recorded at capture time is the only size that is stable, so it is the one
 * the frame sizes from, fed through custom properties because it is a
 * per-instance value no token or utility can name.
 *
 * `maxWidth` / `maxHeight` shrink the frame to fit a column; they never grow
 * it, because a capture enlarged past its recorded box is a blur, and a frame
 * that grows to fill its column is asserting a size the capture never had.
 *
 * The three `state` values swap what fills the frame without touching its
 * geometry — the skeleton reserves exactly the box the raster will land in,
 * and the failure surface leaves exactly the same hole — so nothing on the
 * page moves between "capturing", "captured", and "capture failed".
 *
 * `ready` paints its raster on the same `containerHigh` surface the failure
 * state paints, because a raster is `object-contain` and reaches only as far as
 * its own aspect allows: without a ground the box the geometry guarantees is
 * invisible in the one state a consumer looks at most, so a frame that is
 * behaving correctly reads as three different sizes. Where the raster fills the
 * box it was recorded from the ground is fully covered and nothing changes.
 *
 * @slot - Replaces the raster fill entirely (a `<schmancy-pdf-thumbnail>`, a
 *   canvas, a video). The geometry contract is unchanged.
 */
@customElement('schmancy-media-frame')
export class SchmancyMediaFrame extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				overflow: hidden;
				width: var(--schmancy-media-frame-width, 100%);
				aspect-ratio: var(--schmancy-media-frame-ratio, auto);
			}
		`,
	]

	/** The CSS box recorded at capture time. Without it the frame simply fills its column. */
	@property({ attribute: false }) box?: SchmancyMediaBox

	/** Shrink-to-fit ceiling, in CSS px. Never a floor — the frame does not upscale past `box`. */
	@property({ type: Number, attribute: 'max-width' }) maxWidth = Number.POSITIVE_INFINITY

	/** Shrink-to-fit ceiling, in CSS px. Never a floor. */
	@property({ type: Number, attribute: 'max-height' }) maxHeight = Number.POSITIVE_INFINITY

	/** `ready` shows the raster · `pending` reserves the box · `failed` leaves the same hole with a reason. */
	@property({ type: String, reflect: true }) state: 'ready' | 'pending' | 'failed' = 'ready'

	@property({ type: String }) src = ''

	/** Alt text for the raster. Empty marks it decorative — say so deliberately. */
	@property({ type: String }) alt = ''

	/** What the reader is told when the capture failed. */
	@property({ type: String, attribute: 'failed-label' }) failedLabel = 'Preview unavailable'

	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)

		if (changed.has('state')) this.setAttribute('aria-busy', this.state === 'pending' ? 'true' : 'false')
		if (!changed.has('box') && !changed.has('maxWidth') && !changed.has('maxHeight')) return

		const box = this.box
		if (!box || box.width <= 0 || box.height <= 0) {
			this.style.removeProperty('--schmancy-media-frame-width')
			this.style.removeProperty('--schmancy-media-frame-ratio')
			return
		}
		// One shrink factor for both axes, capped at 1 — the never-upscale rule
		// and the aspect-preserving fit are the same number. Both land as custom
		// properties rather than as literal width/aspect declarations, because
		// the recorded box is a per-instance number no utility or token can name.
		const fit = Math.min(1, this.maxWidth / box.width, this.maxHeight / box.height)
		this.style.setProperty('--schmancy-media-frame-width', `${Math.round(box.width * fit)}px`)
		this.style.setProperty('--schmancy-media-frame-ratio', `${box.width}/${box.height}`)
	}

	override render() {
		return choose(
			this.state,
			[
				['pending', () => html`<schmancy-skeleton class="block h-full w-full rounded-[inherit]"></schmancy-skeleton>`],
				[
					'failed',
					() => html`
						<schmancy-surface
							type="containerHigh"
							class="grid h-full w-full place-items-center gap-1 rounded-[inherit] p-2 text-center"
						>
							<schmancy-icon class="text-surface-onVariant">broken_image</schmancy-icon>
							<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
								${this.failedLabel}
							</schmancy-typography>
						</schmancy-surface>
					`,
				],
			],
			() => html`
				<schmancy-surface type="containerHigh" class="block h-full w-full rounded-[inherit]">
					<slot>
						<img src=${this.src} alt=${this.alt} class="block h-full w-full rounded-[inherit] object-contain" />
					</slot>
				</schmancy-surface>
			`,
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-media-frame': SchmancyMediaFrame
	}
}
